import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Loader2, Plus, Shuffle, Trophy, Send, Trash2, X, CheckCircle2, AlertTriangle, Mail, Lock,
} from 'lucide-react';
import { salesApi, serverError } from '../../lib/salesApi';

const CY = '#90E0EF';
const GOLD = '#e4c07a';
const PURPLE = '#c58bf0';
const inputCls = 'bg-[#0d0d0d] border border-[#222] text-[#f0f0f0] text-sm font-mono px-3 py-2.5 focus:outline-none focus:border-[#90E0EF] placeholder:text-[#555]';

const POOL_LABEL = { sold: 'Bought', gifted_email: 'Gifted · email', gifted_no_email: 'Gifted · no email' };
const POOL_TONE = { sold: CY, gifted_email: PURPLE, gifted_no_email: '#888' };
const EMAIL_STATUS = {
  sent: { label: 'Sent', color: '#7ed4a0' },
  skipped_no_email: { label: 'No email', color: '#888' },
  failed: { label: 'Failed', color: '#e88ba0' },
  not_sent: { label: '—', color: '#555' },
};

export default function SalesDrawsPage() {
  const [draws, setDraws] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tablesMissing, setTablesMissing] = useState(false);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [confirmFinish, setConfirmFinish] = useState(false);

  const [testEmail, setTestEmail] = useState('');
  const [testKind, setTestKind] = useState('winner');
  const [testBusy, setTestBusy] = useState(false);

  const openDraw = useCallback((id) => {
    salesApi.draw(id).then(setSelected).catch((e) => toast.error(serverError(e)));
  }, []);

  const loadList = useCallback((selId) => {
    setLoading(true);
    salesApi.draws()
      .then((d) => { setDraws(d.draws || []); setTablesMissing(false); if (selId) openDraw(selId); })
      .catch((e) => { if (e?.response?.status === 503) setTablesMissing(true); else toast.error(serverError(e)); })
      .finally(() => setLoading(false));
  }, [openDraw]);

  useEffect(() => { loadList(); }, [loadList]);

  // Poll while a finished draw is still dispatching emails.
  useEffect(() => {
    if (selected?.status === 'closed' && selected?.email_dispatch_status === 'sending') {
      const t = setInterval(() => openDraw(selected.id), 3000);
      return () => clearInterval(t);
    }
  }, [selected?.id, selected?.status, selected?.email_dispatch_status, openDraw]);

  async function run() {
    setBusy(true);
    try { const d = await salesApi.runDraw(selected.id); setSelected(d); toast.success('Winners drawn'); }
    catch (e) { toast.error(serverError(e)); } finally { setBusy(false); }
  }

  async function finish() {
    setBusy(true);
    try {
      const d = await salesApi.finishDraw(selected.id);
      setSelected(d); setConfirmFinish(false); loadList();
      toast.success('Draw closed — emails are being sent to participants');
    } catch (e) { toast.error(serverError(e)); } finally { setBusy(false); }
  }

  async function sendTest() {
    if (!testEmail.trim()) return;
    setTestBusy(true);
    try {
      await salesApi.drawTestEmail(selected.id, { email: testEmail.trim(), kind: testKind });
      toast.success(`Sample ${testKind === 'winner' ? 'winner' : 'participant'} email sent to ${testEmail.trim()}`);
    } catch (e) { toast.error(serverError(e)); } finally { setTestBusy(false); }
  }

  async function del(id) {
    if (!window.confirm('Discard this open draw? Its editions become available for a future draw.')) return;
    try {
      await salesApi.deleteDraw(id);
      toast.success('Draw discarded');
      if (selected?.id === id) setSelected(null);
      loadList();
    } catch (e) { toast.error(serverError(e)); }
  }

  if (tablesMissing) {
    return <div className="border border-[#2a2416] bg-[#141207] px-6 py-8 text-center" data-testid="draws-tables-missing">
      <p className="font-outfit text-lg text-[#e4c07a]">Draw tables not initialised</p>
      <p className="text-xs font-mono text-[#aa9] mt-2">Run <span className="text-white">2026_06_27_card_draws.sql</span> on Supabase, then reload.</p>
    </div>;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6" data-testid="draws-page">
      {/* left — draws list */}
      <div>
        <button onClick={() => setShowCreate(true)} data-testid="draws-new-btn"
          style={{ cursor: 'pointer', background: CY, color: '#050505' }}
          className="w-full inline-flex items-center justify-center gap-2 py-3 text-[11px] font-mono uppercase tracking-[0.18em] mb-4">
          <Plus className="w-4 h-4" /> New Draw
        </button>
        <div className="space-y-2" data-testid="draws-list">
          {loading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" style={{ color: CY }} /></div>
          ) : draws.length === 0 ? (
            <p className="px-2 py-6 text-center text-xs font-mono text-[#555]">No draws yet.</p>
          ) : draws.map((d) => {
            const active = selected?.id === d.id;
            return (
              <button key={d.id} onClick={() => openDraw(d.id)} data-testid={`draw-row-${d.id}`}
                style={{ cursor: 'pointer', borderColor: active ? CY : '#1a1a1a' }}
                className="w-full text-left border bg-[#080808] px-4 py-3 transition-colors hover:border-[#333]">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-white truncate font-outfit">{d.name}</span>
                  <span className="text-[9px] font-mono uppercase tracking-[0.12em] px-2 py-0.5 border shrink-0"
                    style={{ color: d.status === 'open' ? GOLD : '#7ed4a0', borderColor: d.status === 'open' ? '#2a2416' : '#16281c' }}>
                    {d.status}
                  </span>
                </div>
                <p className="text-[10px] font-mono text-[#666] mt-1.5">
                  {d.participant_count} in · {d.winner_count} winner{d.winner_count !== 1 ? 's' : ''}
                  {d.status === 'closed' && ` · ${d.emails_sent_count} emailed`}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* right — detail */}
      <div>
        {!selected ? (
          <div className="border border-[#1a1a1a] bg-[#080808] px-6 py-16 text-center" data-testid="draw-empty">
            <Shuffle className="w-8 h-8 mx-auto mb-3" style={{ color: '#333' }} />
            <p className="font-outfit text-base text-[#999]">Select a draw, or start a new one.</p>
            <p className="text-[11px] font-mono text-[#555] mt-2 max-w-md mx-auto leading-relaxed">
              A draw picks winners at random from the editions you choose. Editions used in a past draw never re-enter a new one.
            </p>
          </div>
        ) : (
          <DrawDetail
            draw={selected} busy={busy} onRun={run}
            onOpenFinish={() => setConfirmFinish(true)} onDelete={del}
            testEmail={testEmail} setTestEmail={setTestEmail}
            testKind={testKind} setTestKind={setTestKind}
            testBusy={testBusy} onSendTest={sendTest}
          />
        )}
      </div>

      {showCreate && (
        <CreateDrawModal
          onClose={() => setShowCreate(false)}
          onCreated={(d) => { setShowCreate(false); setSelected(d); loadList(); }}
        />
      )}

      {confirmFinish && selected && (
        <FinishConfirmModal
          draw={selected} busy={busy}
          onCancel={() => setConfirmFinish(false)} onConfirm={finish}
        />
      )}
    </div>
  );
}

function DrawDetail({ draw, busy, onRun, onOpenFinish, onDelete, testEmail, setTestEmail, testKind, setTestKind, testBusy, onSendTest }) {
  const drawn = !!draw.winners_drawn_at;
  const open = draw.status === 'open';
  const sending = draw.status === 'closed' && draw.email_dispatch_status === 'sending';

  return (
    <div className="space-y-5" data-testid="draw-detail">
      {/* header */}
      <div className="border border-[#1a1a1a] bg-[#080808] p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2.5">
              <h2 className="font-outfit text-xl text-white">{draw.name}</h2>
              <span className="text-[9px] font-mono uppercase tracking-[0.12em] px-2 py-0.5 border"
                style={{ color: open ? GOLD : '#7ed4a0', borderColor: open ? '#2a2416' : '#16281c' }}>{draw.status}</span>
            </div>
            <p className="text-[11px] font-mono text-[#666] mt-1.5">
              {draw.participant_count} participants · aiming for {draw.winner_count} winner{draw.winner_count !== 1 ? 's' : ''} · {draw.with_email_count} with email
            </p>
          </div>
          {open && (
            <button onClick={() => onDelete(draw.id)} data-testid={`draw-delete-${draw.id}`} style={{ cursor: 'pointer' }}
              className="text-[#666] hover:text-[#e88ba0] p-1" title="Discard this draw"><Trash2 className="w-4 h-4" /></button>
          )}
        </div>

        {/* action row */}
        {open && (
          <div className="flex items-center gap-3 mt-5 flex-wrap">
            <button onClick={onRun} disabled={busy} data-testid={drawn ? 'draw-rerun-btn' : 'draw-run-btn'}
              style={{ cursor: busy ? 'not-allowed' : 'pointer', background: drawn ? 'transparent' : CY, color: drawn ? CY : '#050505', borderColor: CY }}
              className={`inline-flex items-center gap-2 px-5 py-2.5 text-[11px] font-mono uppercase tracking-[0.16em] disabled:opacity-40 ${drawn ? 'border' : ''}`}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shuffle className="w-4 h-4" />}
              {drawn ? 'Re-run draw' : 'Pick winners'}
            </button>
            {drawn && (
              <button onClick={onOpenFinish} disabled={busy} data-testid="draw-finish-btn"
                style={{ cursor: busy ? 'not-allowed' : 'pointer', background: GOLD, color: '#050505' }}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-[11px] font-mono uppercase tracking-[0.16em] disabled:opacity-40">
                <Lock className="w-4 h-4" /> Finish &amp; send emails
              </button>
            )}
          </div>
        )}
        {sending && (
          <p className="mt-4 text-[11px] font-mono flex items-center gap-2" style={{ color: CY }} data-testid="draw-sending">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Emails are being sent…
          </p>
        )}
        {draw.status === 'closed' && draw.email_dispatch_status === 'done' && (
          <p className="mt-4 text-[11px] font-mono flex items-center gap-2" style={{ color: '#7ed4a0' }} data-testid="draw-sent-summary">
            <CheckCircle2 className="w-3.5 h-3.5" /> {draw.email_summary.sent} sent · {draw.email_summary.skipped_no_email} skipped (no email){draw.email_summary.failed ? ` · ${draw.email_summary.failed} failed` : ''}
          </p>
        )}
      </div>

      {/* winners */}
      {drawn && draw.winners.length > 0 && (
        <div className="border p-5" style={{ borderColor: '#2a2416', background: '#0d0b06' }} data-testid="draw-winners">
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] mb-3 flex items-center gap-2" style={{ color: GOLD }}>
            <Trophy className="w-3.5 h-3.5" /> Winners
          </p>
          <div className="flex flex-wrap gap-2">
            {draw.winners.map((w) => (
              <span key={w.id} data-testid={`draw-winner-${w.card_number_display}`} className="inline-flex items-center gap-2 border px-3 py-1.5" style={{ borderColor: GOLD }}>
                <span className="font-mono text-sm" style={{ color: GOLD }}>#{w.card_number_display}</span>
                <span className="text-[11px] text-[#ccc]">{w.participant_name || 'Unknown'}</span>
                {!w.participant_email && <span className="text-[9px] font-mono text-[#888]">· no email</span>}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* test email */}
      {open && (
        <div className="border border-[#1a1a1a] bg-[#080808] p-5" data-testid="draw-test-panel">
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#666] mb-1 flex items-center gap-2"><Mail className="w-3.5 h-3.5" /> Preview the email</p>
          <p className="text-[10px] font-mono text-[#5a5a5a] mb-4">Send yourself a sample so you can see exactly what participants receive.</p>
          <div className="flex items-center gap-2 flex-wrap">
            <input className={inputCls + ' flex-1 min-w-[180px]'} placeholder="you@example.com" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="draw-test-email-input" />
            <select className={inputCls} value={testKind} onChange={(e) => setTestKind(e.target.value)} style={{ cursor: 'pointer' }} data-testid="draw-test-kind">
              <option value="winner">Winner version</option>
              <option value="participant">Non-winner version</option>
            </select>
            <button onClick={onSendTest} disabled={!testEmail.trim() || testBusy} data-testid="draw-test-send"
              style={{ cursor: testEmail.trim() && !testBusy ? 'pointer' : 'not-allowed' }}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-[11px] font-mono uppercase tracking-[0.16em] border border-[#2a2a2a] text-[#ccc] hover:border-[#90E0EF]/60 hover:text-white disabled:opacity-40">
              {testBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send test
            </button>
          </div>
        </div>
      )}

      {/* participants */}
      <div className="border border-[#1a1a1a]" data-testid="draw-entries">
        <div className="grid grid-cols-[70px_1fr_1fr_100px_90px] gap-2 px-4 py-2.5 border-b border-[#1a1a1a] bg-[#0a0a0a] text-[9px] font-mono uppercase tracking-[0.14em] text-[#666]">
          <span>Edition</span><span>Name</span><span>Email</span><span>Pool</span><span className="text-right">{draw.status === 'closed' ? 'Email' : 'Result'}</span>
        </div>
        {draw.entries.map((e) => {
          const es = EMAIL_STATUS[e.email_status] || EMAIL_STATUS.not_sent;
          return (
            <div key={e.id} data-testid={`draw-entry-${e.card_number_display}`}
              className="grid grid-cols-[70px_1fr_1fr_100px_90px] gap-2 px-4 py-2.5 border-b border-[#141414] items-center"
              style={{ background: e.is_winner ? 'rgba(228,192,122,0.05)' : 'transparent' }}>
              <span className="font-mono text-sm flex items-center gap-1" style={{ color: e.is_winner ? GOLD : CY }}>
                {e.is_winner && <Trophy className="w-3 h-3" style={{ color: GOLD }} />}#{e.card_number_display}
              </span>
              <span className="text-xs text-[#ccc] truncate">{e.participant_name || '—'}</span>
              <span className="text-[11px] font-mono text-[#999] truncate">{e.participant_email || <span className="text-[#555]">no email</span>}</span>
              <span className="text-[10px] font-mono uppercase tracking-[0.08em]" style={{ color: POOL_TONE[e.pool] || '#888' }}>{POOL_LABEL[e.pool] || e.pool}</span>
              <span className="text-right">
                {draw.status === 'closed'
                  ? <span className="text-[10px] font-mono" style={{ color: es.color }}>{es.label}</span>
                  : <span className="text-[10px] font-mono uppercase tracking-[0.1em]" style={{ color: e.is_winner ? GOLD : '#555' }}>{e.is_winner ? 'Winner' : '—'}</span>}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CreateDrawModal({ onClose, onCreated }) {
  const [pools, setPools] = useState(null);
  const [name, setName] = useState('');
  const [winnerCount, setWinnerCount] = useState('3');
  const [incSold, setIncSold] = useState(true);
  const [incGiftEmail, setIncGiftEmail] = useState(true);
  const [incGiftNoEmail, setIncGiftNoEmail] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    salesApi.drawPools().then(setPools).catch((e) => toast.error(serverError(e)));
  }, []);

  const selectedTotal =
    (incSold ? (pools?.sold || 0) : 0) +
    (incGiftEmail ? (pools?.gifted_email || 0) : 0) +
    (incGiftNoEmail ? (pools?.gifted_no_email || 0) : 0);

  async function submit() {
    if (selectedTotal === 0) { toast.error('No eligible editions in the selected pools.'); return; }
    setBusy(true);
    try {
      const d = await salesApi.createDraw({
        name: name.trim() || null,
        winner_count: Math.max(1, parseInt(winnerCount, 10) || 1),
        include_sold: incSold,
        include_gifted_email: incGiftEmail,
        include_gifted_no_email: incGiftNoEmail,
      });
      toast.success(`Draw created with ${d.participant_count} editions`);
      onCreated(d);
    } catch (e) { toast.error(serverError(e)); } finally { setBusy(false); }
  }

  const Pool = ({ id, on, setOn, label, count }) => (
    <button onClick={() => setOn(!on)} data-testid={`pool-${id}-toggle`} style={{ cursor: 'pointer', borderColor: on ? CY : '#222' }}
      className="w-full flex items-center justify-between border bg-[#0d0d0d] px-4 py-3 transition-colors">
      <span className="flex items-center gap-2.5">
        <span className="w-4 h-4 border flex items-center justify-center" style={{ borderColor: on ? CY : '#444', background: on ? CY : 'transparent' }}>
          {on && <CheckCircle2 className="w-3 h-3" style={{ color: '#050505' }} />}
        </span>
        <span className="text-xs font-mono text-[#ddd]">{label}</span>
      </span>
      <span className="text-[11px] font-mono" style={{ color: count ? CY : '#555' }}>{count} eligible</span>
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.72)' }} onClick={() => !busy && onClose()} data-testid="create-draw-modal">
      <div className="w-full max-w-lg border border-[#222] bg-[#0a0a0a] p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <p className="font-outfit text-lg text-white flex items-center gap-2"><Shuffle className="w-4 h-4" style={{ color: CY }} /> New draw</p>
          <button onClick={() => !busy && onClose()} style={{ cursor: 'pointer' }} className="text-[#666] hover:text-white" data-testid="create-draw-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-[9px] font-mono uppercase tracking-[0.18em] text-[#666] mb-1.5">Draw name <span className="text-[#444]">(optional)</span></label>
            <input className={inputCls + ' w-full'} placeholder="e.g. Launch draw — September" value={name} onChange={(e) => setName(e.target.value)} data-testid="draw-name-input" />
          </div>
          <div>
            <label className="block text-[9px] font-mono uppercase tracking-[0.18em] text-[#666] mb-1.5">Number of winners</label>
            <input className={inputCls + ' w-full'} inputMode="numeric" value={winnerCount} onChange={(e) => setWinnerCount(e.target.value.replace(/[^0-9]/g, ''))} data-testid="draw-winner-count" />
            <p className="text-[10px] font-mono text-[#555] mt-1.5">Any number from 1 upward. If it exceeds the participants, everyone wins.</p>
          </div>
          <div>
            <label className="block text-[9px] font-mono uppercase tracking-[0.18em] text-[#666] mb-2">Who takes part</label>
            {!pools ? (
              <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin" style={{ color: CY }} /></div>
            ) : (
              <div className="space-y-2">
                <Pool id="sold" on={incSold} setOn={setIncSold} label="Bought (paid sales)" count={pools.sold} />
                <Pool id="gifted-email" on={incGiftEmail} setOn={setIncGiftEmail} label="Gifted — with email" count={pools.gifted_email} />
                <Pool id="gifted-no-email" on={incGiftNoEmail} setOn={setIncGiftNoEmail} label="Gifted — no email on file" count={pools.gifted_no_email} />
                <p className="text-[10px] font-mono text-[#555] pt-1 leading-relaxed">
                  Editions already used in a previous draw are excluded automatically. No-email entries can win but won't receive an email.
                </p>
              </div>
            )}
          </div>
          <button onClick={submit} disabled={busy || !pools || selectedTotal === 0}
            style={{ cursor: busy || selectedTotal === 0 ? 'not-allowed' : 'pointer', background: CY, color: '#050505' }}
            data-testid="create-draw-submit" className="w-full inline-flex items-center justify-center gap-2 py-3 text-[11px] font-mono uppercase tracking-[0.18em] disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create draw ({selectedTotal} editions)
          </button>
        </div>
      </div>
    </div>
  );
}

function FinishConfirmModal({ draw, busy, onCancel, onConfirm }) {
  const [agreed, setAgreed] = useState(false);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.78)' }} onClick={() => !busy && onCancel()} data-testid="finish-confirm-modal">
      <div className="w-full max-w-md border p-6" style={{ borderColor: '#2a2416', background: '#0a0a0a' }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2.5 mb-3">
          <AlertTriangle className="w-5 h-5" style={{ color: GOLD }} />
          <p className="font-outfit text-lg text-white">Finish this draw?</p>
        </div>
        <p className="text-[12px] font-mono text-[#aaa] leading-relaxed mb-1">
          This <strong className="text-white">closes the draw permanently</strong> and emails all {draw.participant_count} participants
          ({draw.with_email_count} have an email) — winners are told how to claim their print, everyone else gets a thank-you.
        </p>
        <p className="text-[11px] font-mono text-[#666] leading-relaxed mb-4">
          The {draw.winners.length} winning edition{draw.winners.length !== 1 ? 's' : ''} and every participant are logged, so these editions can't enter a future draw. This can't be undone.
        </p>
        <label className="flex items-start gap-2.5 mb-5 cursor-pointer" data-testid="finish-confirm-checkbox-label">
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid="finish-confirm-checkbox" className="mt-0.5" style={{ accentColor: GOLD, cursor: 'pointer' }} />
          <span className="text-[11px] font-mono text-[#ccc] leading-relaxed">I understand this closes the draw and sends the emails now.</span>
        </label>
        <div className="flex items-center gap-3">
          <button onClick={onCancel} disabled={busy} data-testid="finish-cancel" style={{ cursor: busy ? 'not-allowed' : 'pointer' }}
            className="flex-1 py-2.5 text-[11px] font-mono uppercase tracking-[0.16em] border border-[#2a2a2a] text-[#aaa] hover:text-white disabled:opacity-40">Cancel</button>
          <button onClick={onConfirm} disabled={busy || !agreed} data-testid="finish-confirm-submit"
            style={{ cursor: busy || !agreed ? 'not-allowed' : 'pointer', background: GOLD, color: '#050505' }}
            className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 text-[11px] font-mono uppercase tracking-[0.16em] disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Confirm &amp; send
          </button>
        </div>
      </div>
    </div>
  );
}
